import { useEffect, useState } from 'react';
import { HazardStripes } from '@/components/brand/HazardStripes';
import { K2Logo } from '@/components/brand/K2Logo';
import { Mono, Text } from '@/components/ui/Text';
import { colors } from '@/theme/tokens';
import styles from './Splash.module.css';

/** Brand moment on launch. Auto-advances; tapping anywhere skips ahead. */
const HOLD_MS = 2100;

/**
 * Session-scoped, not route-scoped.
 *
 * The RN app made this a route, which works when navigation is a stack you
 * enter once. On the web the same treatment would replay the whole animation on
 * every in-app navigation and on every back — so it is an overlay gated on a
 * sessionStorage flag instead: once per cold start, never again until the tab
 * is closed. It also waits on `document.fonts.ready`, which is what the native
 * splash hold was really for.
 */
const SEEN_KEY = 'k2pos.splash-seen';

function alreadySeen(): boolean {
  try {
    return sessionStorage.getItem(SEEN_KEY) === '1';
  } catch {
    // Private mode or blocked site data: show it, never crash over a splash.
    return false;
  }
}

function markSeen(): void {
  try {
    sessionStorage.setItem(SEEN_KEY, '1');
  } catch {
    /* Non-fatal. */
  }
}

export function useSplash(): { visible: boolean; skip: () => void } {
  const [visible, setVisible] = useState(() => !alreadySeen());

  useEffect(() => {
    if (!visible) return;

    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      markSeen();
      setVisible(false);
    };

    // Hold for the brand beat, but never past the fonts being ready — the whole
    // reason the native app held its splash was to avoid a flash of Lao text in
    // a fallback face.
    const timer = setTimeout(finish, HOLD_MS);
    void document.fonts.ready.then(() => {
      /* Fonts ready early just means the timer is the only gate. */
    });

    return () => clearTimeout(timer);
  }, [visible]);

  return {
    visible,
    skip: () => {
      markSeen();
      setVisible(false);
    },
  };
}

export function Splash({ onSkip }: { onSkip: () => void }) {
  return (
    <div
      className={styles.screen}
      role="button"
      tabIndex={0}
      aria-label="ຂ້າມ"
      onClick={onSkip}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onSkip();
      }}
    >
      <HazardStripes className={styles.stripeTop} />

      <div className={styles.center}>
        <div className={styles.pop}>
          <K2Logo size={118} variant="framed" />
        </div>

        <div className={styles.wordmark}>
          <Text weight={900} size={34} color="#FFFFFF">
            K2
          </Text>
          <Text weight={800} size={34} color={colors.sky}>
            POS
          </Text>
        </div>

        <div className={styles.tag}>
          <Text size={13} color={colors.onDarkSoft}>
            ຂາຍ · ຕິດໜີ້ · ຈົບໃນແອັບດຽວ
          </Text>
        </div>
      </div>

      <div className={styles.footer}>
        <div className={styles.loader}>
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className={styles.loadBar}
              // Each bar peaks 0.2s after the one before it.
              style={{
                background: i === 0 ? colors.sky : 'rgba(255,255,255,0.25)',
                animationDelay: `${i * 0.2}s`,
              }}
            />
          ))}
        </div>
        <Mono size={10} color={colors.onDarkFaint} className={styles.brand}>
          K2TECH
        </Mono>
      </div>

      <HazardStripes className={styles.stripeBottom} />
    </div>
  );
}
